import React, { useState, useEffect } from 'react';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/AppIcon';
import { apiService } from '../../services/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const DashboardPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, merchants } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams();
        const merchantsList = Array.isArray(merchants) && merchants?.length > 0 ? merchants : [];
        merchantsList?.forEach(m => qs?.append('merchants', m));
        const queryString = qs?.toString();
        const url = queryString ? `/portal/dashboard?${queryString}` : '/portal/dashboard';
        const response = await apiService?.get(url);
        const details = response?.data?.details?.[0];
        setDashboardData(details || null);
      } catch (err) {
        setError(err?.response?.data?.status_description || err?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [merchants]);

  const balances = dashboardData?.balances || [];
  const totalDeposits = dashboardData?.total_deposits || [];
  const totalPayouts = dashboardData?.total_payouts || [];
  const totalDepositsToday = dashboardData?.total_deposits_today || [];
  const totalPayoutsToday = dashboardData?.total_payouts_today || [];
  const approvals = dashboardData?.approvals || {};
  const payoutsPastSevenDays = dashboardData?.payouts_past_seven_days || [];
  const depositsPastSevenDays = dashboardData?.deposits_past_seven_days || [];

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return Number(amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (val) => {
    if (val === undefined || val === null) return '0.00%';
    return `${Number(val)?.toFixed(2)}%`;
  };

  const primaryCurrency = balances?.[0]?.currency_code || totalDeposits?.[0]?.currency_code || 'KES';

  // Generate last 7 days as actual dates (today = index 6, oldest = index 0)
  const getLast7Days = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d?.setDate(d?.getDate() - i);
      const day = String(d?.getDate())?.padStart(2, '0');
      const month = d?.toLocaleString('en-US', { month: 'short' });
      const isoDate = d?.toISOString()?.slice(0, 10); // "YYYY-MM-DD"
      days?.push({ label: `${day}-${month}`, isoDate });
    }
    return days;
  };

  const last7Days = getLast7Days();

  const buildChartData = (apiArray) => {
    const map = {};
    (apiArray || [])?.forEach(item => {
      if (item?.transaction_date) {
        map[item.transaction_date] = Number(item?.total_amount) || 0;
      }
    });
    return last7Days?.map(({ label, isoDate }) => ({
      name: label,
      value: map?.[isoDate] ?? 0,
    }));
  };

  const depositChartData = buildChartData(depositsPastSevenDays);
  const payoutChartData = buildChartData(payoutsPastSevenDays);

  const summaryCards = [
    {
      label: 'Total Deposits',
      value: totalDeposits?.[0] ? `${totalDeposits?.[0]?.currency_code} ${formatAmount(totalDeposits?.[0]?.total_amount)}` : '—',
      icon: 'DollarSign',
      bg: 'bg-[#3d6b5e]',
      iconBg: 'bg-white/20',
    },
    {
      label: 'Total Payouts',
      value: totalPayouts?.[0] ? `${totalPayouts?.[0]?.currency_code} ${formatAmount(totalPayouts?.[0]?.total_amount)}` : '—',
      icon: 'TrendingUp',
      bg: 'bg-[#d4a017]',
      iconBg: 'bg-white/20',
    },
    {
      label: 'Available Balance',
      value: balances?.[0] ? `${balances?.[0]?.currency_code} ${formatAmount(balances?.[0]?.total_available_balance)}` : '—',
      icon: 'Wallet',
      bg: 'bg-[#2d2d2d]',
      iconBg: 'bg-white/20',
    },
  ];

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6 max-w-[1400px]">

          {/* Page Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#2d2d2d]">All Summaries</h1>
              <p className="text-sm text-[#8b7d7d] mt-0.5">
                Welcome back, {user?.first_name || user?.email_address || 'User'}! Here's what's happening with your account.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-text-primary bg-surface hover:bg-muted transition-smooth shadow-subtle">
                <Icon name="Download" size={15} className="text-text-secondary" />
                Export to CSV
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-text-primary bg-surface hover:bg-muted transition-smooth shadow-subtle">
                <Icon name="Calendar" size={15} className="text-text-secondary" />
                Select date range
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-text-secondary">Loading dashboard...</span>
            </div>
          )}

          {error && (
            <div className="bg-error/10 border border-error/30 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {!loading && (
            <>
              {/* Bold Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
                {summaryCards?.map((card, i) => (
                  <div key={i} className={`${card?.bg} rounded-2xl p-6 flex items-center justify-between shadow-moderate`}>
                    <div>
                      <p className="text-white/70 text-sm font-medium mb-1">{card?.label}</p>
                      <p className="text-white text-2xl font-bold tracking-tight">{card?.value}</p>
                    </div>
                    <div className={`${card?.iconBg} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon name={card?.icon} size={22} className="text-white" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Approval Rates */}
              <div className="bg-surface border border-border rounded-2xl p-6 mb-5 shadow-subtle">
                <h2 className="text-base font-bold text-text-primary mb-5 flex items-center gap-2">
                  <Icon name="PieChart" size={18} className="text-[#3d6b5e]" />
                  Approval Rates
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-[#3d6b5e]/5 border border-[#3d6b5e]/20 rounded-xl">
                    <div className="w-11 h-11 bg-[#3d6b5e]/15 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle" size={20} className="text-[#3d6b5e]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#3d6b5e]">{formatPercent(approvals?.success_percentage)}</p>
                      <p className="text-xs text-text-secondary">Success</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-[#d4a017]/5 border border-[#d4a017]/20 rounded-xl">
                    <div className="w-11 h-11 bg-[#d4a017]/15 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="Clock" size={20} className="text-[#d4a017]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#d4a017]">{formatPercent(approvals?.processing_percentage)}</p>
                      <p className="text-xs text-text-secondary">Processing</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-[#cd853f]/5 border border-[#cd853f]/20 rounded-xl">
                    <div className="w-11 h-11 bg-[#cd853f]/15 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="XCircle" size={20} className="text-[#cd853f]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#cd853f]">{formatPercent(approvals?.failed_percentage)}</p>
                      <p className="text-xs text-text-secondary">Failed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Balance Cards */}
              {balances?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                  {balances?.map((bal, i) => (
                    <React.Fragment key={i}>
                      <div className="bg-surface border border-border rounded-2xl p-5 shadow-subtle">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Available Balance</span>
                          <span className="text-xs font-bold text-[#3d6b5e] bg-[#3d6b5e]/10 px-2 py-0.5 rounded-full">{bal?.currency_code}</span>
                        </div>
                        <p className="text-2xl font-bold text-text-primary">{formatAmount(bal?.total_available_balance)}</p>
                      </div>
                      <div className="bg-surface border border-border rounded-2xl p-5 shadow-subtle">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Held in Processing</span>
                          <span className="text-xs font-bold text-[#d4a017] bg-[#d4a017]/10 px-2 py-0.5 rounded-full">{bal?.currency_code}</span>
                        </div>
                        <p className="text-2xl font-bold text-[#d4a017]">{formatAmount(bal?.total_held_in_processing)}</p>
                      </div>
                      <div className="bg-surface border border-border rounded-2xl p-5 shadow-subtle">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Held Chargeback</span>
                          <span className="text-xs font-bold text-[#cd853f] bg-[#cd853f]/10 px-2 py-0.5 rounded-full">{bal?.currency_code}</span>
                        </div>
                        <p className="text-2xl font-bold text-[#cd853f]">{formatAmount(bal?.total_held_chargeback)}</p>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Chart Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                {/* Payment Flow Chart */}
                <div className="bg-surface border border-border rounded-2xl p-6 shadow-subtle">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-bold text-text-primary">Payment Flow</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={depositChartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="depositGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3d6b5e" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#3d6b5e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8dfdf" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8b7d7d' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#8b7d7d' }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => v >= 1000 ? `${(v / 1000)?.toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ background: '#faf8f5', border: '1px solid #e8dfdf', borderRadius: 8, fontSize: 12 }}
                        formatter={(value) => [formatAmount(value), 'Deposits']}
                      />
                      <Area type="monotone" dataKey="value" stroke="#3d6b5e" strokeWidth={2} fill="url(#depositGrad)" dot={false} activeDot={{ r: 4, fill: '#3d6b5e' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Payouts Flow Chart */}
                <div className="bg-surface border border-border rounded-2xl p-6 shadow-subtle">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-bold text-text-primary">Payouts Flow</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={payoutChartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="payoutGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#d4a017" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#d4a017" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8dfdf" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8b7d7d' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#8b7d7d' }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => v >= 1000 ? `${(v / 1000)?.toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ background: '#faf8f5', border: '1px solid #e8dfdf', borderRadius: 8, fontSize: 12 }}
                        formatter={(value) => [formatAmount(value), 'Payouts']}
                      />
                      <Area type="monotone" dataKey="value" stroke="#d4a017" strokeWidth={2} fill="url(#payoutGrad)" dot={false} activeDot={{ r: 4, fill: '#d4a017' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Quick Access */}
              <div className="bg-surface border border-border rounded-2xl p-6 shadow-subtle">
                <h2 className="text-base font-bold text-text-primary mb-4">Quick Access</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {[
                    { label: 'Deposits', path: '/transactions/deposits', icon: 'ArrowDownCircle', color: 'bg-[#3d6b5e]/10 text-[#3d6b5e]' },
                    { label: 'Payouts', path: '/transactions/payouts', icon: 'ArrowUpCircle', color: 'bg-[#d4a017]/10 text-[#d4a017]' },
                    { label: 'Settlements', path: '/payment-operations/settlements', icon: 'Banknote', color: 'bg-primary/10 text-primary' },
                    { label: 'Refunds', path: '/payment-operations/refunds', icon: 'RotateCcw', color: 'bg-[#cd853f]/10 text-[#cd853f]' },
                    { label: 'API Keys', path: '/api-keys', icon: 'Key', color: 'bg-[#d4a017]/10 text-[#d4a017]' },
                    { label: 'Merchants', path: '/merchants', icon: 'Store', color: 'bg-primary/10 text-primary' },
                    { label: 'Approval', path: '/onboarding/approval', icon: 'CheckSquare', color: 'bg-[#3d6b5e]/10 text-[#3d6b5e]' },
                    { label: 'Roles', path: '/rbac/roles', icon: 'UserCog', color: 'bg-[#cd853f]/10 text-[#cd853f]' },
                  ]?.map((item, i) => (
                    <a key={i} href={item?.path} className="flex flex-col items-center p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-muted transition-smooth">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 ${item?.color}`}>
                        <Icon name={item?.icon} size={18} />
                      </div>
                      <span className="text-xs font-medium text-text-primary text-center leading-tight">{item?.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}

          {!loading && !dashboardData && !error && (
            <div className="flex flex-col items-center justify-center py-16 text-text-secondary">
              <Icon name="LayoutDashboard" size={40} className="mb-3 opacity-30" />
              <p>No dashboard data available.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
